import { useState, useRef, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, RotateCcw, X, Plus, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { api } from '../../lib/api'

interface PreSelectedAssignment {
  id: number
  source_type: string  // "calendar_event" | "assignment" | "agenda_homework"
  name: string
  subject: string | null
  course_name: string | null
}

interface StudentQuickScanResultProps {
  images: string[]
  preSelectedAssignment: PreSelectedAssignment | null  // null = "Other"
  onConfirm: (xpAwarded: number) => void
  onRetake: () => void  // Retake the current page
  onCancel: () => void
  onAddPage: () => void
}

export function StudentQuickScanResult({
  images,
  preSelectedAssignment,
  onConfirm,
  onRetake,
  onCancel,
  onAddPage,
}: StudentQuickScanResultProps) {
  const queryClient = useQueryClient()
  const [currentPage, setCurrentPage] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const uploadStarted = useRef(false)

  // Background upload mutation - fire and forget
  const uploadMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/student/quick-scan', {
        image_data: images[0],
        additional_pages: images.slice(1),
        page_count: images.length,
        pre_selected_assignment_id: preSelectedAssignment?.id || null,
        source_type: preSelectedAssignment?.source_type || 'calendar_event',
        pre_selected_subject: preSelectedAssignment?.subject || null,
      })
      return response.data
    },
    onSuccess: () => {
      // Refresh data in background
      queryClient.invalidateQueries({ queryKey: ['student-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['student-scans'] })
      queryClient.invalidateQueries({ queryKey: ['expected-assignments'] })
    },
    onError: (error) => {
      // Log error but don't block the user - they already got their feedback
      console.error('Background scan upload failed:', error)
    },
  })

  // Save immediately - optimistic UX
  const handleSave = () => {
    setIsSaving(true)

    // Start background upload if not already started
    if (!uploadStarted.current) {
      uploadStarted.current = true
      uploadMutation.mutate()
    }

    // Optimistic XP: base 25 + 10 for pre-selected match
    const estimatedXp = preSelectedAssignment ? 35 : 25

    // Small delay to show the saving state, then confirm
    setTimeout(() => {
      onConfirm(estimatedXp)
    }, 300)
  }

  // Show last captured page by default
  useEffect(() => {
    setCurrentPage(images.length - 1)
  }, [images.length])

  return (
    <div className="fixed inset-0 z-50 bg-surface-base/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-surface-card">
        <div className="flex-1 min-w-0">
          {preSelectedAssignment ? (
            <>
              <h2 className="text-lg font-semibold truncate">{preSelectedAssignment.name}</h2>
              <p className="text-sm text-gray-400 truncate">{preSelectedAssignment.course_name}</p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold">Other Homework</h2>
              <p className="text-sm text-gray-400">We'll figure out what this is</p>
            </>
          )}
        </div>
        <button
          onClick={onCancel}
          className="p-2 rounded-lg hover:bg-surface-hover transition-colors ml-2"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Image preview - main content */}
      <div className="flex-1 flex flex-col p-4 overflow-hidden">
        <div className="flex-1 relative rounded-xl overflow-hidden bg-surface-card">
          <img
            src={images[currentPage]}
            alt={`Page ${currentPage + 1}`}
            className="w-full h-full object-contain"
          />

          {/* Page navigation for multi-page */}
          {images.length > 1 && (
            <>
              {currentPage > 0 && (
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-surface-card/80 rounded-full hover:bg-surface-hover"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}
              {currentPage < images.length - 1 && (
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-surface-card/80 rounded-full hover:bg-surface-hover"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-surface-card/90 rounded-full text-sm">
                Page {currentPage + 1} of {images.length}
              </div>
            </>
          )}
        </div>

        {/* Page thumbnails for multi-page */}
        {images.length > 1 && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentPage(idx)}
                className={`flex-shrink-0 w-14 h-14 rounded-lg border-2 overflow-hidden ${
                  idx === currentPage ? 'border-quest-primary' : 'border-gray-700/50'
                }`}
              >
                <img src={img} alt={`Page ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
            <button
              onClick={onAddPage}
              className="flex-shrink-0 w-14 h-14 rounded-lg border-2 border-dashed border-gray-600 hover:border-gray-500 flex items-center justify-center text-gray-500 hover:text-gray-400"
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="p-4 bg-surface-card border-t border-gray-700/50">
        <div className="flex gap-3">
          {/* Retake button */}
          <button
            onClick={onRetake}
            disabled={isSaving}
            className="btn-secondary flex items-center justify-center gap-2 px-4"
          >
            <RotateCcw className="w-5 h-5" />
            Retake
          </button>

          {/* Add page button (single page only) */}
          {images.length === 1 && (
            <button
              onClick={onAddPage}
              disabled={isSaving}
              className="btn-secondary flex items-center justify-center gap-2 px-4"
            >
              <Plus className="w-5 h-5" />
              Add Page
            </button>
          )}

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Check className="w-5 h-5" />
            )}
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
